import axios, { AxiosInstance, InternalAxiosRequestConfig } from 'axios'

const ADMIN_API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '') || 'https://api.verrafied.ai') +
  '/api/v1/admin'

const ACCESS_TOKEN_KEY = 'admin_access_token'
const REFRESH_TOKEN_KEY = 'admin_refresh_token'

export const tokenStorage = {
  getAccess: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  set: (access: string, refresh: string) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, access)
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh)
  },
  clear: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
  },
}

const adminApiClient: AxiosInstance = axios.create({
  baseURL: ADMIN_API_BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
})

adminApiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccess()
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  return config
})

let isRefreshing = false
let refreshQueue: Array<(token: string) => void> = []

adminApiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config

    if (error.response?.status !== 401 || original._retry) {
      return Promise.reject(error)
    }

    const errorCode = error.response?.data?.errorCode
    if (errorCode === 'REFRESH_EXPIRED' || errorCode === 'REFRESH_INVALID') {
      tokenStorage.clear()
      window.location.href = '/admin/login?reason=session_expired'
      return Promise.reject(error)
    }

    if (isRefreshing) {
      return new Promise((resolve) => {
        refreshQueue.push((token) => {
          original.headers.Authorization = `Bearer ${token}`
          resolve(adminApiClient(original))
        })
      })
    }

    original._retry = true
    isRefreshing = true

    try {
      const refreshToken = tokenStorage.getRefresh()
      if (!refreshToken) {
        tokenStorage.clear()
        window.location.href = '/admin/login'
        return Promise.reject(error)
      }

      const { data } = await axios.post(
        `${ADMIN_API_BASE_URL}/auth/refresh`,
        null,
        { params: { refreshToken } }
      )

      tokenStorage.set(data.accessToken, data.refreshToken)
      refreshQueue.forEach((cb) => cb(data.accessToken))
      refreshQueue = []

      original.headers.Authorization = `Bearer ${data.accessToken}`
      return adminApiClient(original)
    } catch (refreshError) {
      tokenStorage.clear()
      window.location.href = '/admin/login?reason=session_expired'
      return Promise.reject(refreshError)
    } finally {
      isRefreshing = false
    }
  }
)

export default adminApiClient
